/**
 * @description API service for Outreach — email activity, templates, and logo management
 *
 * Key features:
 * - Activity: paginated email sends log with filters + summary stats
 * - Templates: CRUD for email templates (company + location overrides)
 * - Logo: upload/delete email branding logo
 *
 * @dependencies
 * - apiClient: Axios instance with auth headers
 *
 * @notes
 * - All activity queries are scoped server-side by sender_context + company_id
 * - Employee users only see their own sends (enforced by backend, not frontend)
 * - Template keys follow pattern 'email_templates.{type}' but the frontend
 *   passes just the type (e.g., 'invoice') — the route prefixes it
 */

import apiClient from './apiClient';

const outreachService = {
  // ===========================================================================
  // Activity
  // ===========================================================================

  /**
   * Get paginated email activity log
   * @param {Object} params - { page, limit, type, status, dateFrom, dateTo, search }
   * @returns {Promise<{ data: Array, total: number, page: number, limit: number, totalPages: number }>}
   */
  getActivity: async (params = {}) => {
    const response = await apiClient.get('/outreach/activity', { params });
    return response.data;
  },

  /**
   * Get activity summary stats for dashboard cards
   * @param {number} [days=30] - Lookback window
   * @returns {Promise<{ totalSent, deliveredCount, deliveredPercent, openedCount, openedPercent, bouncedCount, bouncedPercent }>}
   */
  getActivityStats: async (days = 30) => {
    const response = await apiClient.get('/outreach/activity/stats', { params: { days } });
    return response.data;
  },

  /**
   * Get a single email send record
   * @param {number} id - Email send ID
   * @returns {Promise<Object>} Email send record
   */
  getActivityById: async (id) => {
    const response = await apiClient.get(`/outreach/activity/${id}`);
    return response.data;
  },

  // ===========================================================================
  // Templates
  // ===========================================================================

  /**
   * Get all email template definitions with resolved company values
   * @param {string} [locationId] - Optional location ID for location-scoped resolution
   * @returns {Promise<{ templates: Array }>}
   */
  getTemplates: async (locationId) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.get('/outreach/templates', { params });
    return response.data;
  },

  /**
   * Get a single template with resolved value
   * @param {string} templateKey - Template type key (e.g., 'invoice', 'reminder', 'receipt')
   * @param {string} [locationId] - Optional location ID
   * @returns {Promise<Object>} Resolved template value
   */
  getTemplate: async (templateKey, locationId) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.get(`/outreach/templates/${templateKey}`, { params });
    return response.data;
  },

  /**
   * Update an email template (company or location level)
   * @param {string} templateKey - Template type key
   * @param {Object} data - { subject, body, locationId? }
   * @returns {Promise<Object>}
   */
  updateTemplate: async (templateKey, data) => {
    const response = await apiClient.put(`/outreach/templates/${templateKey}`, data);
    return response.data;
  },

  /**
   * Reset a template to platform default
   * @param {string} templateKey - Template type key
   * @param {string} [locationId] - If provided, resets location override only
   * @returns {Promise<Object>}
   */
  resetTemplate: async (templateKey, locationId) => {
    const params = locationId ? { locationId } : {};
    const response = await apiClient.delete(`/outreach/templates/${templateKey}`, { params });
    return response.data;
  },

  // ===========================================================================
  // Logo
  // ===========================================================================

  /**
   * Upload email logo
   * @param {File} file - Image file (PNG, JPEG, SVG — max 2MB)
   * @param {Function} [onProgress] - Axios onUploadProgress callback
   * @returns {Promise<{ url: string, size: number, mimeType: string }>}
   */
  uploadLogo: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post('/outreach/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
    });
    return response.data;
  },

  /**
   * Delete email logo
   * @returns {Promise<Object>}
   */
  deleteLogo: async () => {
    const response = await apiClient.delete('/outreach/logo');
    return response.data;
  }
};

export default outreachService;
